<template>
  <basic-dialog
    :show="show"
    @close="$emit('close')"
    max-width="1100px"
    draggable
  >
    <template v-slot:title>{{ title }}</template>
    <v-row justify="center" align="center" v-if="$apollo.loading">
      <v-progress-circular indeterminate></v-progress-circular>
    </v-row>
    <v-container v-else fluid pa-0>
      <v-tabs
        background-color="white"
        color="deep-purple accent-4"
        grow
        v-model="selectedTab"
        align-with-title
      >
        <v-tab>Details</v-tab>
        <v-tab
          :disabled="id == ''"
          v-if="
            $store.getters.hasAccessTo(
              'administration_panel:user_access_control'
            )
          "
          >Access Settings</v-tab
        >
        <v-tab :disabled="id == ''">Custom Fields</v-tab>
        <v-tab
          :disabled="id == ''"
          v-if="$store.getters.hasAccessTo('plugins:third_party_setups')"
          >Third Party Setup
        </v-tab>
        <v-tab-item>
          <v-row>
            <v-col sm="4">
              <v-text-field
                label="Name"
                v-model="name"
                :outlined="outlinedInputs"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                label="Username"
                v-model="username"
                :outlined="outlinedInputs"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                label="Password"
                type="password"
                v-model="password"
                :outlined="outlinedInputs"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row row wrap>
            <v-col sm="4">
              <v-select
                :items="userTypesList"
                v-model="typeId"
                label="User type"
                :outlined="outlinedInputs"
                item-value="id"
                item-text="name"
              ></v-select>
            </v-col>
            <v-col sm="4">
              <v-text-field
                label="Email"
                v-model="email"
                :outlined="outlinedInputs"
                :rules="[rules.email]"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                label="Telephone"
                v-model="telephone"
                :outlined="outlinedInputs"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row row wrap>
            <v-col sm="4">
              <department-list-select
                :outlined="outlinedInputs"
                v-model="department"
              ></department-list-select>
            </v-col>
            <v-col sm="4">
              <destination-list-select
                v-model="destinationId"
                :outlined="outlinedInputs"
              ></destination-list-select>
            </v-col>
            <v-col sm="4">
              <brand-list-select
                v-model="brand"
                :outlined="outlinedInputs"
              ></brand-list-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                label="Notes"
                v-model="notes"
                :outlined="outlinedInputs"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-checkbox
                v-model="sendItineraryNotification"
                label="Send Itinerary Email Notification"
                primary
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col>
              <v-checkbox
                v-model="isActive"
                label="Active"
                primary
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col>
              <v-checkbox
                v-model="notInUse"
                label="Not in Use"
                primary
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col>
              <v-checkbox
                v-model="isSupervisor"
                label="Supervisor"
                primary
                hide-details
                v-if="$store.getters.userConfig.isSupervisor"
              ></v-checkbox>
            </v-col>
            <v-col>
              <v-checkbox
                v-if="$store.getters.hasAccessTo('agents:edit_verified')"
                v-model="emailVerified"
                label="Email Verified"
                primary
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-tab-item>
        <v-tab-item
          v-if="
            $store.getters.hasAccessTo(
              'administration_panel:user_access_control'
            )
          "
        >
          <access-settings-editor
            v-model="accessSettings"
          ></access-settings-editor>
        </v-tab-item>
        <v-tab-item>
          <custom-fields-form
            hide-save
            v-model="customFields"
          ></custom-fields-form>
        </v-tab-item>
        <v-tab-item
          v-if="$store.getters.hasAccessTo('plugins:third_party_setups')"
        >
          <third-party-setups-for-level level="STAFF" :owner-id="id" />
        </v-tab-item>
      </v-tabs>
    </v-container>
    <template v-slot:actions>
      <v-btn
        color="primary"
        v-if="showSave"
        :disabled="!isValid"
        @click="onSave"
        >Save</v-btn
      >
    </template>
    <div v-if="debug">
      <code>staff: {{ value }}</code>
    </div>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../shared/BasicDialog";
import DepartmentListSelect from "../shared/DepartmentListSelect";
import {
  STAFF_RECORD_QUERY,
  USER_TYPES_QUERY,
} from "../../../constants/graphql";
import AccessSettingsEditor from "./AccessSettingsEditor";
import CustomFieldsForm from "../shared/CustomFieldsForm";
import { removeGQLTypename } from "../../../helpers";
import DestinationListSelect from "../shared/DestinationListSelect";
import BrandListSelect from "../shared/BrandListSelect";
import ThirdPartySetupsForLevel from "../shared/third-party-setups/ThirdPartySetupsForLevel.vue";
export default {
  name: "agent-staff-edit-dialog",

  props: {
    value: {
      type: Object,
      default: () => null,
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },

  components: {
    BasicDialog,
    DepartmentListSelect,
    AccessSettingsEditor,
    CustomFieldsForm,
    DestinationListSelect,
    BrandListSelect,
    ThirdPartySetupsForLevel,
  },

  data() {
    return {
      debug: false,
      selectedTab: 0,
      id: "",
      name: "",
      username: "",
      password: "",
      telephone: "",
      department: "",
      email: "",
      typeId: "",
      customFields: [],
      accessSettings: [],
      sendItineraryNotification: false,
      isActive: this.value && this.value.isActive ? true : false,
      notInUse: this.value && this.value.notInUse ? true : false,
      isSupervisor: this.value && this.value.isSupervisor ? true : false,
      emailVerified: this.value && this.value.emailVerified ? true : false,
      destinationId: "",
      brand: "",
      notes: "",
      rules: {
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return value != "" ? pattern.test(value) || "Invalid e-mail." : true;
        },
      },
    };
  },

  watch: {
    value(newVal) {
      if (newVal.id && newVal.id != "") {
        this.id = newVal.id;
        this.isSupervisor = newVal.isSupervisor ? true : false;
        this.emailVerified = newVal.emailVerified ? true : false;
        this.notInUse = newVal.notInUse ? true : false;
        this.isActive = newVal.isActive ? true : false;
        //refresh user query
        this.$apollo.queries.user.refetch();
      } else {
        //reset
        this.id = "";
        this.name = "";
        this.username = "";
        this.password = "";
        this.telephone = "";
        this.department = "";
        this.email = "";
        this.typeId = "";
        this.sendItineraryNotification = false;
        this.isActive = false; //http://tracker.globetrack.ie/issues/6184
        this.notInUse = false;
        this.accessSettings = [];
        this.customFields = [];
        this.notes = "";
        this.emailVerified = false;
      }
      this.selectedTab = 0;
    },
    user(newVal) {
      //update staff basic props
      for (const f in newVal) {
        if (this[f] != undefined && !Array.isArray(this[f])) {
          this[f] = newVal[f];
        }
      }
      this.password = "";
      this.sendItineraryNotification =
        newVal.config && newVal.config.sendItineraryNotification;
      //update custom fields and access settings
      this.accessSettings = newVal.accessSettings
        ? newVal.accessSettings.map(removeGQLTypename)
        : [];
      this.customFields = newVal.customFields
        ? newVal.customFields.edges.map(removeGQLTypename)
        : [];
    },
  },

  computed: {
    showSave() {
      //return !this.$store.getters.hasAccessTo("agents:view_only") && this.$store.getters.hasAccessTo("agents:edit_user");
      if (this.agent && this.agent.isOwner) {
        //https://tracker.globetrack.ie/issues/7735
        return this.$store.getters.hasAccessTo("agents:edit_user");
      }
      return true;
    },
    userIsSupervisor() {
      return (
        this.value &&
        this.value.isSupervisor &&
        this.value.isSupervisor.toString() == "true"
      );
    },
    staff() {
      return {
        id: this.id != "" ? this.id : undefined,
        name: this.name,
        username: this.username,
        password: this.password != "" ? this.password : undefined,
        email: this.email,
        telephone: this.telephone,
        typeId: this.typeId,
        department: this.department,
        brand: this.brand,
        destinationId: this.destinationId,
        customFields:
          this.customFields.length > 0 ? this.customFields : undefined,
        accessSettings:
          this.accessSettings.length > 0 ? this.accessSettings : undefined,
        config: { sendItineraryNotification: this.sendItineraryNotification },
        notInUse: this.notInUse,
        isActive: this.isActive,
        isSupervisor: this.isSupervisor,
        emailVerified: this.emailVerified,
        notes: this.notes,
      };
    },
    title() {
      return this.name == "" ? "New Staff" : this.name;
    },
    isValid() {
      return (
        this.name != "" &&
        this.username != "" &&
        this.email != "" &&
        this.typeId != "" &&
        this.department != ""
      );
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    userTypesList() {
      return this.userTypes && this.userTypes.edges
        ? this.userTypes.edges.map(removeGQLTypename)
        : [];
    },
  },

  methods: {
    onSave() {
      this.$emit("save", { ...this.staff });
    },
  },
  apollo: {
    user: {
      query: STAFF_RECORD_QUERY,
      variables() {
        return {
          id: this.id,
        };
      },
      skip() {
        return this.id == "";
      },
      fetchPolicy: "network-only",
    },
    userTypes: {
      query: USER_TYPES_QUERY,
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/agents/AgentStaffEditDialog.vue